import type { CodeRead } from "@openapi";
import { useCallback, useMemo, useState } from "react";

/** Rows shown at first and added on each "more". */
export const CODE_LIST_PAGE_SIZE = 50;

export type LastUsedOrder = "descend" | "ascend";

export interface CodeListFilter {
    /** Part of the name, case-insensitive; blank for all. */
    term?: string;
    /** Code types to keep; empty for all. */
    types?: string[];
    order?: LastUsedOrder;
}

/** What a code list row needs: a whole code, or the server's lighter item. */
export type CodeRow = Pick<CodeRead, "id" | "name" | "type" | "code"> & {
    created_at?: Date;
    last_used?: Date;
};

const lastUsedTime = (code: CodeRow): number =>
    code.last_used ? new Date(code.last_used).getTime() : 0;

/**
 * The codes whose name contains `term` (case-insensitive, trimmed) and
 * whose type is one of `types`, newest first by last use, or oldest first
 * with `order` "ascend".
 */
export function filterCodes<Row extends CodeRow>(
    codes: Row[],
    { term = "", types = [], order = "descend" }: CodeListFilter = {},
): Row[] {
    const needle = term.trim().toLowerCase();
    const matching = codes.filter(
        (code) =>
            (!needle || code.name.toLowerCase().includes(needle)) &&
            (types.length === 0 || types.includes(code.type)),
    );
    const sign = order === "descend" ? -1 : 1;
    return matching.sort((a, b) => sign * (lastUsedTime(a) - lastUsedTime(b)));
}

export interface CodeListWindow<Row extends CodeRow = CodeRow> {
    /** The rows to render: the first `shown` of the matching codes. */
    visible: Row[];
    /** How many codes match the filter. */
    total: number;
    hasMore: boolean;
    /** Shows the next page of matching codes. */
    showMore: () => void;
}

const filterKey = ({ term = "", types = [] }: CodeListFilter): string =>
    `${term}\u0000${[...types].sort().join(",")}`;

/**
 * The matching codes a page at a time, for a list that loads more as the
 * user scrolls. A changed search or type filter starts again from the
 * first page; one seen before continues where it was.
 */
export function useCodeListWindow<Row extends CodeRow>(
    codes: Row[],
    filter: CodeListFilter = {},
): CodeListWindow<Row> {
    const key = filterKey(filter);
    // The count belongs to the filter it was reached with; another filter
    // starts over without an effect.
    const [reached, setReached] = useState({
        key,
        count: CODE_LIST_PAGE_SIZE,
    });
    const count = reached.key === key ? reached.count : CODE_LIST_PAGE_SIZE;

    const { term, types, order } = filter;
    const matching = useMemo(
        () => filterCodes(codes, { term, types, order }),
        [codes, term, types, order],
    );
    const showMore = useCallback(
        () => setReached({ key, count: count + CODE_LIST_PAGE_SIZE }),
        [key, count],
    );

    return {
        visible: matching.slice(0, count),
        total: matching.length,
        hasMore: count < matching.length,
        showMore,
    };
}
